import { ApplicationInsights } from "@microsoft/applicationinsights-web";
import { configValue } from "@/services/config";
import { generateW3CId } from "@microsoft/applicationinsights-core-js";
import { useAuthenticatedUserStore } from "@/modules/authentication/authentication-store";

let _instance;

export const VUE_APPLICATION_INSIGHTS = Symbol("VUE_APPLICATION_INSIGHTS");

function createApplicationInstanceVuePlugin(applicationInsights) {
  return {
    install: (app) => {
      app.provide(VUE_APPLICATION_INSIGHTS, applicationInsights);
    },
  };
}

function resolveConfiguration() {
  return {
    applicationVersion: configValue("VUE_APP_APPLICATION_VERSION"),
    cloudRoleName: configValue("VUE_APP_AZURE_APP_INSIGHT_APP"),
    connectionString: configValue("APPLICATIONINSIGHTS_CONNECTION_STRING"),
  };
}

export function createApplicationInsights(router) {
  if (_instance) {
    throw new Error("ApplicationInsights already initialized");
  }

  const config = resolveConfiguration();

  _instance = new ApplicationInsights({
    config: {
      connectionString: config.connectionString,
      enableAjaxErrorStatusText: true,
      enableAutoRouteTracking: false,
      autoTrackPageVisitTime: true,
      enableCorsCorrelation: true,
      correlationHeaderExcludedDomains: ["googleapis.com"],
      enableRequestHeaderTracking: true,
      enableResponseHeaderTracking: true,
    },
  });

  _instance.loadAppInsights();
  _instance.addTelemetryInitializer(setupTelemetry(config));
  setupPageTracking(_instance, router, config.cloudRoleName);

  return {
    applicationInsights: _instance,
    applicationInsightsPlugin: createApplicationInstanceVuePlugin(_instance),
  };
}

function setupPageTracking(applicationInsights, router, cloudRoleName) {
  router.beforeEach((route, from, next) => {
    applicationInsights.context.telemetryTrace.traceID = generateW3CId();
    applicationInsights.context.telemetryTrace.name = route.name ?? undefined;
    applicationInsights.startTrackPage(cloudRoleName + " / " + route.name);
    next();
  });

  router.afterEach((route) => {
    const name = cloudRoleName + " / " + route.name;
    applicationInsights.stopTrackPage(name, location.href);
    applicationInsights.flush();
  });
}

function setupTelemetry(config) {
  const store = useAuthenticatedUserStore();

  return function (envelope) {
    //const userRoles = store.state.userData.roles || [];
    //const user = store.state.userData.user?.profile?.sub;
    //const companyId = store.state.userData.companyid;

    envelope.tags["ai.cloud.role"] = config.cloudRoleName;
    envelope.tags["ai.application.ver"] = config.applicationVersion;
    envelope.data["userProfession"] = store.mainRole;

    if (store.user.activeCompany) {
      envelope.data["companyId"] = store.user.activeCompany.id;
    }

    if (store.user.isAuthenticated) {
      envelope.tags["ai.user.authUserId"] = store.user.profile.userId;
    }
  };
}

export function getApplicationInsightsInstance() {
  if (!_instance) {
    throw new Error("Applications insights is not configured");
  }
  return _instance;
}
